// src/components/admin/YouTubeVideosManagement.js
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

const YouTubeVideosManagement = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [currentItem, setCurrentItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterLanguage, setFilterLanguage] = useState('');
  const [stats, setStats] = useState({ total: 0, published: 0, featured: 0, categories: 0, languages: 0 });

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    video_url: '',
    video_id: '',
    thumbnail_url: '',
    language: 'English',
    category: '',
    series_name: '',
    duration: '',
    is_featured: false,
    is_published: true,
    published_date: '',
    display_order: 0
  });

  const languages = ['English', 'French', 'Portuguese', 'Arabic', 'Swahili', 'Spanish'];
  const categories = ['General', 'Clinical Training', 'Health Education', 'Health Awareness', 'Pediatric Care', 'Professional Development'];

  useEffect(() => {
    fetchData();
    fetchStats();
  }, [searchTerm, filterCategory, filterLanguage]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('adminToken');

      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }
      let url = `${API_BASE}/admin/youtube-videos?`;
      if (searchTerm) url += `search=${encodeURIComponent(searchTerm)}&`;
      if (filterCategory) url += `category=${encodeURIComponent(filterCategory)}&`;
      if (filterLanguage) url += `language=${filterLanguage}&`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      const data = await response.json();
      if (data.success) {
        setVideos(data.data.videos || []);
      } else {
        toast.error(data.message || 'Failed to fetch videos');
      }
    } catch (err) {
      console.error('Error:', err);
      toast.error('Failed to fetch videos');
    } finally {
      if (loading) setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }
      const response = await fetch(`${API_BASE}/admin/youtube-videos/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) setStats(data.data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleCreate = () => {
    setModalMode('create');
    setCurrentItem(null);
    setFormData({
      title: '', description: '', video_url: '', video_id: '', thumbnail_url: '',
      language: 'English', category: '', series_name: '', duration: '',
      is_featured: false, is_published: true, published_date: '', display_order: 0
    });
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setModalMode('edit');
    setCurrentItem(item);
    setFormData({
      title: item.title || '',
      description: item.description || '',
      video_url: item.video_url || '',
      video_id: item.video_id || '',
      thumbnail_url: item.thumbnail_url || '',
      language: item.language || 'English',
      category: item.category || '',
      series_name: item.series_name || '',
      duration: item.duration || '',
      is_featured: item.is_featured === 1,
      is_published: item.is_published === 1,
      published_date: item.published_date ? item.published_date.substring(0, 10) : '',
      display_order: item.display_order || 0
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this video?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }
      const response = await fetch(`${API_BASE}/admin/youtube-videos/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        fetchData();
        fetchStats();
        toast.success('Video deleted successfully');
      } else {
        toast.error(data.message || 'Failed to delete video');
      }
    } catch (err) {
      toast.error('Failed to delete video');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('adminToken');
      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }
      const url = modalMode === 'create'
        ? `${API_BASE}/admin/youtube-videos`
        : `${API_BASE}/admin/youtube-videos/${currentItem.id}`;

      const response = await fetch(url, {
        method: modalMode === 'create' ? 'POST' : 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        setShowModal(false);
        fetchData();
        fetchStats();
        toast.success(`Video ${modalMode === 'create' ? 'created' : 'updated'} successfully`);
      } else {
        toast.error(data.message || 'Operation failed');
      }
    } catch (err) {
      toast.error('Operation failed');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  if (loading && videos.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border" style={{ color: '#6B8E23' }} role="status"></div>
        <span className="ms-3 text-muted">Loading...</span>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="h3 fw-bold mb-1" style={{ color: '#1f2937' }}>YouTube Videos Management</h2>
          <p className="text-muted mb-0">Manage ARCA educational videos</p>
        </div>
        <button onClick={handleCreate} className="btn fw-bold shadow-sm" style={{ backgroundColor: '#6B8E23', color: 'white' }}>
          <i className="bi bi-plus-circle me-2"></i>Add Video
        </button>
      </div>

      {/* Stats */}
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="card border-0 shadow-sm" style={{ borderLeft: '4px solid #6B8E23' }}>
            <div className="card-body"><small className="text-muted">Total Videos</small><h4 className="fw-bold mb-0" style={{ color: '#6B8E23' }}>{stats.total || 0}</h4></div>
          </div>
        </div>
        <div className="col-md-2">
          <div className="card border-0 shadow-sm" style={{ borderLeft: '4px solid #4CAF50' }}>
            <div className="card-body"><small className="text-muted">Published</small><h4 className="fw-bold mb-0" style={{ color: '#4CAF50' }}>{stats.published || 0}</h4></div>
          </div>
        </div>
        <div className="col-md-2">
          <div className="card border-0 shadow-sm" style={{ borderLeft: '4px solid #FF9800' }}>
            <div className="card-body"><small className="text-muted">Featured</small><h4 className="fw-bold mb-0" style={{ color: '#FF9800' }}>{stats.featured || 0}</h4></div>
          </div>
        </div>
        <div className="col-md-2">
          <div className="card border-0 shadow-sm" style={{ borderLeft: '4px solid #2196F3' }}>
            <div className="card-body"><small className="text-muted">Categories</small><h4 className="fw-bold mb-0" style={{ color: '#2196F3' }}>{stats.categories || 0}</h4></div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm" style={{ borderLeft: '4px solid #9C27B0' }}>
            <div className="card-body"><small className="text-muted">Languages</small><h4 className="fw-bold mb-0" style={{ color: '#9C27B0' }}>{stats.languages || 0}</h4></div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <input type="text" className="form-control" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </div>
            <div className="col-md-4">
              <select className="form-select" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                <option value="">All Categories</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="col-md-4">
              <select className="form-select" value={filterLanguage} onChange={(e) => setFilterLanguage(e.target.value)}>
                <option value="">All Languages</option>
                {languages.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Video Grid */}
      <div className="row g-4">
        {videos.length === 0 ? (
          <div className="col-12 text-center py-5 text-muted">No videos found</div>
        ) : (
          videos.map((item) => (
            <div key={item.id} className="col-md-6 col-lg-4 col-xl-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="position-relative">
                  <img
                    src={item.thumbnail_url || `https://img.youtube.com/vi/${item.video_id}/mqdefault.jpg`}
                    alt={item.title}
                    className="card-img-top"
                    style={{ height: '180px', objectFit: 'cover' }}
                    onError={(e) => e.target.src = 'https://via.placeholder.com/320x180?text=No+Thumbnail'}
                  />
                  {item.is_featured === 1 && (
                    <span className="position-absolute top-0 end-0 m-2 badge bg-warning">Featured</span>
                  )}
                  {item.language && item.language !== 'English' && (
                    <span className="position-absolute top-0 start-0 m-2 badge" style={{ backgroundColor: '#6B8E23' }}>{item.language}</span>
                  )}
                </div>
                <div className="card-body">
                  <h6 className="fw-bold mb-2" style={{ fontSize: '0.9rem', minHeight: '40px' }}>{item.title.substring(0, 60)}{item.title.length > 60 ? '...' : ''}</h6>
                  {item.category && <span className="badge bg-info mb-2">{item.category}</span>}
                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <span className={`badge ${item.is_published ? 'bg-success' : 'bg-secondary'}`}>
                      {item.is_published ? 'Published' : 'Draft'}
                    </span>
                    <div className="btn-group btn-group-sm">
                      <button
                        onClick={() => handleEdit(item)}
                        className="btn btn-sm"
                        style={{ backgroundColor: '#2196F3', color: 'white', border: 'none' }}
                      >
                        <i className="bi bi-pencil"></i> Edit
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="btn btn-sm"
                        style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', marginLeft: '4px' }}
                      >
                        <i className="bi bi-trash"></i> Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#6B8E23', color: 'white' }}>
                <h5 className="modal-title">{modalMode === 'create' ? 'Add Video' : 'Edit Video'}</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label">Title <span className="text-danger">*</span></label>
                      <input type="text" className="form-control" name="title" value={formData.title} onChange={handleInputChange} required />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Video URL <span className="text-danger">*</span></label>
                      <input type="url" className="form-control" name="video_url" value={formData.video_url} onChange={handleInputChange} required placeholder="https://youtu.be/..." />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Video ID</label>
                      <input type="text" className="form-control" name="video_id" value={formData.video_id} onChange={handleInputChange} placeholder="Auto-extracted from URL" />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Language</label>
                      <select className="form-select" name="language" value={formData.language} onChange={handleInputChange}>
                        {languages.map(l => <option key={l} value={l}>{l}</option>)}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Category</label>
                      <select className="form-select" name="category" value={formData.category} onChange={handleInputChange}>
                        <option value="">Select...</option>
                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Series</label>
                      <input type="text" className="form-control" name="series_name" value={formData.series_name} onChange={handleInputChange} />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Thumbnail URL</label>
                      <input type="url" className="form-control" name="thumbnail_url" value={formData.thumbnail_url} onChange={handleInputChange} placeholder="Auto-generated if empty" />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Description</label>
                      <textarea className="form-control" name="description" value={formData.description} onChange={handleInputChange} rows="3"></textarea>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Published Date</label>
                      <input type="date" className="form-control" name="published_date" value={formData.published_date} onChange={handleInputChange} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Display Order</label>
                      <input type="number" className="form-control" name="display_order" value={formData.display_order} onChange={handleInputChange} min="0" />
                    </div>
                    <div className="col-md-6">
                      <div className="form-check form-switch">
                        <input className="form-check-input" type="checkbox" name="is_published" checked={formData.is_published} onChange={handleInputChange} id="pub-check" />
                        <label className="form-check-label" htmlFor="pub-check">Published</label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-check form-switch">
                        <input className="form-check-input" type="checkbox" name="is_featured" checked={formData.is_featured} onChange={handleInputChange} id="feat-check" />
                        <label className="form-check-label" htmlFor="feat-check">Featured</label>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn" style={{ backgroundColor: '#6B8E23', color: 'white' }} onClick={handleSubmit}>
                  {modalMode === 'create' ? 'Create' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default YouTubeVideosManagement;
